<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

import CanvasLegend from '@/components/maps_canvases/CanvasLegend.vue';
import RenderCanvas from '@/components/renderCanvases/RenderCanvas.vue';
import CircleDotSolid from '@/components/svg/CircleDotSolid.vue';
import OnboardingTooltip from '@/components/utilities/OnboardingTooltip.vue';
import type { AvailableLocales, LocaleSchema } from '@/locales/index.ts';
import type { Building } from '@/typings/buildings/Building';
import type { BuildingRenderLayer } from '@/typings/drawing/BuildingRenderLayer';
import type { Project } from '@/typings/projects/Project';
import type { Unit } from '@/typings/units/Unit';
import { getBackgroundImageUrl } from '@/utils/canvas';
import { ListingTypeEnum } from '@/enums/ListingType';

const { t } = useI18n<{message: LocaleSchema}, AvailableLocales>({
  useScope: 'global',
});

const canvasLegendItems = computed(() => {
  const baseItems = [
    {
      label: t('browsingApp.statuses.buildingStatus.Available'),
      color: 'green'
    },
    {
      label: t('browsingApp.statuses.buildingStatus.Full'),
      color: 'red'
    }
  ];

  const thirdItem = {
    label: props.flowType === 'rental' ? t('browsingApp.statuses.buildingStatus.Purchase') : t('browsingApp.statuses.buildingStatus.Rental'),
    color: 'purple'
  };

  return [...baseItems, thirdItem];
});

const selectedBuilding = defineModel<Building | undefined>('selectedBuilding');
const hoveredBuilding = defineModel<Building | undefined>('hoveredBuilding');


const props = defineProps<{
  buildingLayers: BuildingRenderLayer[];
  buildings: Building[];
  project?: Project;
  hoveredUnit?: Unit;
  flowType?: 'rental' | 'purchase';
}>();

const emit = defineEmits<{
  'canvas-loaded': [];
  'building-click': [layer: BuildingRenderLayer];
}>();

const canvasLoaded = ref(false);
const showOnboarding = ref(false);
const canvasElement = useTemplateRef<HTMLDivElement>('canvasElement');

const projectPlanImageUrl = computed(() => {
  return props.project ? getBackgroundImageUrl(props.project.planImageUrl) : '';
});

const canvasFlowClass = computed(() => {
  const baseClass = 'renderCanvas_project';
  return props.flowType ? `${baseClass} renderCanvas_project--${props.flowType}` : baseClass;
});

const isLayerSelected = (layer: BuildingRenderLayer) => {
  return selectedBuilding.value?.id === layer.id;
};

const isLayerHovered = (layer: BuildingRenderLayer) => {
  return hoveredBuilding.value?.id === layer.id;
};

function handleBuildingClick(layer: BuildingRenderLayer) {
  emit('building-click', layer);
}

function handleBuildingHover(layer: BuildingRenderLayer | undefined) {
  if (!layer) {
    hoveredBuilding.value = undefined;
    return;
  }
  
  const building = props.buildings.find(b => b.id === layer.id);
  if (building) {
    hoveredBuilding.value = building;
  }
}


function onCanvasLoaded() {
  canvasLoaded.value = true;
  emit('canvas-loaded');
  
  setTimeout(() => {
    showOnboarding.value = true;
  }, 500);
}

function getBuildingAriaLabel(layer: BuildingRenderLayer) {
  return `Bâtiment ${layer.buildingNo}, statut: ${t(`browsingApp.statuses.buildingStatus.${layer.status}`)}`;
}

</script>

<template>
  <div ref="canvasElement"
       class="renderCanvas-wrapper">
    <RenderCanvas :class="canvasFlowClass"
                  :layers="buildingLayers"
                  :image-url="projectPlanImageUrl"
                  @canvas-loaded="onCanvasLoaded">
      <template #point="{ layer, point, handleClick }">
        <g class="renderCanvas-pointItem"
           :data-availability-status="layer.status"
           :data-listing-type="layer.listingType"
           :data-selected="isLayerSelected(layer)"
           :data-hovered="isLayerHovered(layer)"
           :data-unit-highlighted="hoveredUnit?.buildingId === layer.id">
          <foreignObject
            :x="point.points[0].x - 16"
            :y="point.points[0].y - 16"
            width="32"
            height="32"
            class="renderCanvas-polygonShape"
            role="button"
            :tabindex="0"
            :aria-label="getBuildingAriaLabel(layer)"
            :aria-pressed="isLayerSelected(layer)"
            @click="handleBuildingClick(layer)"
            @mouseenter="handleBuildingHover(layer)"
            @mouseleave="handleBuildingHover(undefined)"
            @keydown.enter="handleBuildingClick(layer)"
            @keydown.space="handleBuildingClick(layer)">
            <div class="renderCanvas-pointIcon">
              <CircleDotSolid />
            </div>
          </foreignObject>
        </g>
      </template>
    </RenderCanvas>
    <CanvasLegend :items="canvasLegendItems" />
    <OnboardingTooltip :visible="showOnboarding"
                       :to-click-element="canvasElement ?? undefined"
                       always-show
                       message-key="onboarding.canvas.buildingSelection"
                       storage-key="gesteco-onboarding-seen-project" />
  </div>
</template>

<style lang="scss">
.renderCanvas-wrapper {
  position: relative;
}

/* Couleurs par défaut Achat */
.renderCanvas_project [data-availability-status="Upcoming"] {
  --baseColor: var(--clrYellowStatus);
}
.renderCanvas_project [data-availability-status="Reserved"] {
  --baseColor: var(--clrOrangeStatus);
}
.renderCanvas_project [data-availability-status="Full"] {
  --baseColor: var(--clrRedStatus);
}
.renderCanvas_project [data-availability-status="Available"] {
  --baseColor: var(--clrGreenStatus);
}
.renderCanvas_project [data-listing-type="Rental"] {
  --baseColor: var(--clrPurpleStatus);
}



/* Couleurs spécifiques au flow Location */
.renderCanvas_project--rental [data-listing-type="Sale"][data-availability-status="Available"]{
  --baseColor: var(--clrPurpleStatus);
}
.renderCanvas_project--rental [data-listing-type="Rental"][data-availability-status="Available"]{
  --baseColor: var(--clrGreenStatus);
}
.renderCanvas_project--rental [data-listing-type="Rental"][data-availability-status="Upcoming"]{
  --baseColor: var(--clrYellowStatus);
}
.renderCanvas_project--rental [data-listing-type="Rental"][data-availability-status="Reserved"] {
  --baseColor: var(--clrOrangeStatus);
}
.renderCanvas_project--rental [data-listing-type="Rental"][data-availability-status="Full"] {
  --baseColor: var(--clrRedStatus);
  pointer-events: none;
}
</style>
